import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import OrderDetails from './pages/OrderDetails';
import Drivers from './pages/Drivers';
import TourGuides from './pages/TourGuides';
import TourCompanies from './pages/TourCompanies';
import Services from './pages/Services';
import Blog from './pages/Blog';
import Affiliates from './pages/Affiliates';
import Users from './pages/Users';
import Roles from './pages/Roles';
import Coupons from './pages/Coupons';
import Reviews from './pages/Reviews';
import Reports from './pages/Reports';
import Analytics from './pages/Analytics';
import Leaderboard from './pages/Leaderboard';
import DriverStats from './pages/DriverStats';
import TourGuideStats from './pages/TourGuideStats';
import TourCompanyStats from './pages/TourCompanyStats';
import SystemHealth from './pages/SystemHealth';
import Backups from './pages/Backups';
import EmailTemplates from './pages/EmailTemplates';
import WhatsAppTemplates from './pages/WhatsAppTemplates';
import Profile from './pages/Profile';
import ActivityLog from './pages/ActivityLog';
import Settings from './pages/Settings';
import BlogComments from './pages/BlogComments';
import NewsletterSubscribers from './pages/NewsletterSubscribers';
import PromotionalEmails from './pages/PromotionalEmails';
import { Toaster } from 'react-hot-toast';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { hasPermission } = useAuth();

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/:id" element={<OrderDetails />} />
        <Route path="/drivers" element={<Drivers />} />
        <Route path="/tour-guides" element={<TourGuides />} />
        <Route path="/tour-companies" element={<TourCompanies />} />
        <Route path="/services" element={<Services />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/drivers/:id/stats" element={<DriverStats />} />
        <Route path="/tour-guides/:id/stats" element={<TourGuideStats />} />
        <Route path="/tour-companies/:id/stats" element={<TourCompanyStats />} />
        <Route path="/blog/comments" element={<BlogComments />} />
        <Route path="/newsletter" element={<NewsletterSubscribers />} />
        <Route path="/promotional-emails" element={<PromotionalEmails />} />
        {hasPermission('affiliates_read') && <Route path="/affiliates" element={<Affiliates />} />}
        {hasPermission('users_read') && <Route path="/users" element={<Users />} />}
        {hasPermission('roles_read') && <Route path="/roles" element={<Roles />} />}
        {hasPermission('coupons_read') && <Route path="/coupons" element={<Coupons />} />}
        {hasPermission('reviews_read') && <Route path="/reviews" element={<Reviews />} />}
        {hasPermission('reports_view') && <Route path="/reports" element={<Reports />} />}
        {hasPermission('reports_view') && <Route path="/analytics" element={<Analytics />} />}
        {hasPermission('reports_view') && <Route path="/leaderboard" element={<Leaderboard />} />}
        {hasPermission('system_read') && <Route path="/system-health" element={<SystemHealth />} />}
        {hasPermission('backup_read') && <Route path="/backups" element={<Backups />} />}
        {hasPermission('settings_read') && <Route path="/templates" element={<EmailTemplates />} />}
        {hasPermission('settings_read') && <Route path="/whatsapp-templates" element={<WhatsAppTemplates />} />}
        <Route path="/profile" element={<Profile />} />
        {hasPermission('activity_read') && <Route path="/activity-log" element={<ActivityLog />} />}
        {hasPermission('settings_read') && <Route path="/settings" element={<Settings />} />}
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppRoutes />
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
